import {
  CATEGORIES,
  POSES,
  PREP_ITEMS,
  REFS,
  SCENE_GUIDES,
  SCENE_LABELS,
  SHORT_PACKS,
  STATS,
  SUGGESTED_EIGHT,
} from '../data/poses.js';
import { store } from './utils/store.js';
import { $, escapeHtml, showToast } from './utils/dom.js';

const BASE = import.meta.env.BASE_URL || '/';

const state = {
  category: 'all',
  query: '',
  favOnly: false,
  coupleOnly: false,
  activePoseId: null,
  shootIds: [],
  shootIndex: 0,
};

function assetUrl(path) {
  const clean = String(path || '').replace(/^\//, '');
  return `${BASE}${clean}`;
}

function poseImage(pose) {
  const src = pose.image || `poses/${pose.id}.png`;
  return assetUrl(src);
}

function poseVisual(pose, className = 'pose-visual') {
  return `
    <figure class="${className}">
      <img
        src="${escapeHtml(poseImage(pose))}"
        alt="${escapeHtml(pose.name)}"
        loading="lazy"
        decoding="async"
        width="600"
        height="800"
      />
    </figure>`;
}

function difficultyDots(n) {
  return Array.from({ length: 3 }, (_, i) =>
    `<span class="diff__dot ${i < n ? 'is-on' : ''}"></span>`
  ).join('');
}

function poseScenes(pose) {
  return Array.isArray(pose.scenes) ? pose.scenes : pose.category ? [pose.category] : [];
}

function matchesScene(pose, sceneId) {
  if (sceneId === 'all') return true;
  const scenes = poseScenes(pose);
  if (sceneId === 'couple') {
    return pose.couple === true || scenes.includes('couple');
  }
  return scenes.includes(sceneId);
}

function filterPoses() {
  const q = state.query.trim().toLowerCase();
  const favs = store.getFavorites();

  return POSES.filter((p) => {
    if (!matchesScene(p, state.category)) return false;
    if (state.favOnly && !favs.has(p.id)) return false;
    if (state.coupleOnly && !p.couple) return false;
    if (!q) return true;
    const sceneText = poseScenes(p)
      .map((s) => SCENE_LABELS[s] || s)
      .join(' ');
    const hay = [p.name, p.summary, sceneText, ...(p.tags || [])].join(' ').toLowerCase();
    return hay.includes(q);
  });
}

function renderFilters() {
  const root = $('#category-filters');
  root.innerHTML = CATEGORIES.map(
    (c) => `
    <button
      type="button"
      class="chip ${state.category === c.id ? 'is-active' : ''}"
      role="tab"
      aria-selected="${state.category === c.id}"
      data-category="${c.id}"
    >${escapeHtml(c.label)}</button>`
  ).join('');
  renderSceneGuide();
}

function renderSceneGuide() {
  const el = $('#scene-guide');
  if (!el) return;
  const tip = SCENE_GUIDES[state.category];
  if (!tip) {
    el.hidden = true;
    el.textContent = '';
    return;
  }
  el.hidden = false;
  el.textContent = tip;
}

function renderIntroStats() {
  const poseEl = $('#stat-poses');
  const sceneEl = $('#stat-scenes');
  if (poseEl) poseEl.textContent = String(STATS.poseCount);
  if (sceneEl) sceneEl.textContent = String(STATS.sceneCount);
}

function sceneTagsHtml(pose) {
  const labels = poseScenes(pose)
    .filter((s) => s !== 'couple' || !pose.couple)
    .slice(0, 2)
    .map((s) => SCENE_LABELS[s] || s);
  const tags = [];
  if (pose.couple) tags.push('双人');
  labels.forEach((l) => {
    if (l !== '双人甜蜜') tags.push(l);
  });
  return tags
    .slice(0, 3)
    .map((t) => `<span class="tag">${escapeHtml(t)}</span>`)
    .join('');
}

function renderPoseCard(pose) {
  const fav = store.isFavorite(pose.id);
  const inList = pose.id in store.getChecklist();

  return `
  <article class="pose-card" data-pose-id="${pose.id}" tabindex="0" role="button" aria-label="查看 ${escapeHtml(pose.name)}">
    <div class="pose-card__art">
      ${poseVisual(pose, 'pose-visual pose-visual--card')}
    </div>
    <div class="pose-card__meta">
      <div class="pose-card__top">
        <h3 class="pose-card__name">${escapeHtml(pose.name)}</h3>
        <button
          type="button"
          class="icon-btn ${fav ? 'is-on' : ''}"
          data-fav="${pose.id}"
          aria-label="${fav ? '取消收藏' : '收藏'}"
          aria-pressed="${fav}"
        >♥</button>
      </div>
      <p class="pose-card__summary">${escapeHtml(pose.summary)}</p>
      <div class="pose-card__foot">
        <span class="diff" title="难度">${difficultyDots(pose.difficulty)}</span>
        <span class="pose-card__time">${escapeHtml(pose.duration)}</span>
        ${sceneTagsHtml(pose)}
        ${inList ? '<span class="tag tag--list">已在清单</span>' : ''}
      </div>
    </div>
  </article>`;
}

function renderGrid() {
  const list = filterPoses();
  const grid = $('#pose-grid');
  const empty = $('#pose-empty');

  if (!list.length) {
    grid.innerHTML = '';
    empty.hidden = false;
    return;
  }

  empty.hidden = true;
  grid.innerHTML = list.map(renderPoseCard).join('');
}

function openDrawer(poseId) {
  const pose = POSES.find((p) => p.id === poseId);
  if (!pose) return;

  state.activePoseId = poseId;
  const fav = store.isFavorite(pose.id);
  const inList = pose.id in store.getChecklist();
  const body = $('#drawer-body');

  body.innerHTML = `
    <div class="drawer__art">${poseVisual(pose, 'pose-visual pose-visual--drawer')}</div>
    <p class="drawer__eyebrow">${pose.couple ? '双人姿势' : '单人姿势'} · ${escapeHtml(pose.duration)}</p>
    <h2 class="drawer__title" id="drawer-title">${escapeHtml(pose.name)}</h2>
    <p class="drawer__summary">${escapeHtml(pose.summary)}</p>

    <div class="drawer__actions">
      <button type="button" class="btn btn--ghost btn--sm" data-fav="${pose.id}">
        ${fav ? '已收藏 ♥' : '收藏 ♡'}
      </button>
      <button type="button" class="btn btn--primary btn--sm" data-add-list="${pose.id}">
        ${inList ? '已在必拍清单' : '加入必拍清单'}
      </button>
      <button type="button" class="btn btn--ghost btn--sm" data-shoot-one="${pose.id}">
        拍摄此姿势
      </button>
    </div>

    <h3 class="drawer__h">三步摆法</h3>
    <ol class="drawer__steps">
      ${pose.steps.map((s) => `<li>${escapeHtml(s)}</li>`).join('')}
    </ol>

    <h3 class="drawer__h">避坑提示</h3>
    <ul class="drawer__tips">
      ${pose.tips.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}
    </ul>

    <p class="drawer__camera"><strong>镜头建议：</strong>${escapeHtml(pose.camera)}</p>

    <div class="drawer__tags">
      ${pose.tags.map((t) => `<span class="tag">${escapeHtml(t)}</span>`).join('')}
    </div>
  `;

  const drawer = $('#pose-drawer');
  drawer.hidden = false;
  requestAnimationFrame(() => drawer.classList.add('is-open'));
  document.body.classList.add('drawer-open');
}

function closeDrawer() {
  const drawer = $('#pose-drawer');
  drawer.classList.remove('is-open');
  document.body.classList.remove('drawer-open');
  setTimeout(() => {
    drawer.hidden = true;
    state.activePoseId = null;
  }, 280);
}

function renderChecklist() {
  const map = store.getChecklist();
  const ids = Object.keys(map);
  const list = $('#checklist-list');
  const bar = $('#checklist-bar');
  const label = $('#checklist-label');

  if (!ids.length) {
    list.innerHTML = `<li class="checklist__empty">清单还是空的。选一组「短拍快选」，或从姿势库点「加入必拍」。</li>`;
    bar.style.width = '0%';
    label.textContent = '已完成 0 / 0';
    return;
  }

  const done = ids.filter((id) => map[id]).length;
  const pct = Math.round((done / ids.length) * 100);
  bar.style.width = `${pct}%`;
  label.textContent = `已完成 ${done} / ${ids.length}`;

  list.innerHTML = ids
    .map((id) => {
      const pose = POSES.find((p) => p.id === id);
      if (!pose) return '';
      return `
      <li class="checklist__item ${map[id] ? 'is-done' : ''}">
        <label>
          <input type="checkbox" data-check="${id}" ${map[id] ? 'checked' : ''} />
          <span class="checklist__name">${escapeHtml(pose.name)}</span>
          <span class="checklist__cat">${pose.couple ? '双人' : '单人'} · ${escapeHtml(pose.duration)}</span>
        </label>
        <button type="button" class="icon-btn" data-remove-list="${id}" aria-label="移出清单">×</button>
      </li>`;
    })
    .join('');
}

function renderPrep() {
  const root = $('#prep-grid');
  root.innerHTML = PREP_ITEMS.map(
    (block) => `
    <article class="prep-card">
      <h3>${escapeHtml(block.title)}</h3>
      <ul>
        ${block.items.map((i) => `<li>${escapeHtml(i)}</li>`).join('')}
      </ul>
    </article>`
  ).join('');
}

function renderRefs() {
  const root = $('#refs-grid');
  root.innerHTML = REFS.map(
    (r) => `
    <figure class="ref-card ref-card--${escapeHtml(r.tone)}">
      <img src="${escapeHtml(assetUrl(r.src))}" alt="${escapeHtml(r.title)}" loading="lazy" width="800" height="1000" />
      <figcaption>
        <strong>${escapeHtml(r.title)}</strong>
        <span>${escapeHtml(r.caption)}</span>
      </figcaption>
    </figure>`
  ).join('');
}

function getShootQueue() {
  const map = store.getChecklist();
  const ids = Object.keys(map);
  if (ids.length) return ids;
  return [...SUGGESTED_EIGHT];
}

function renderShoot() {
  const id = state.shootIds[state.shootIndex];
  const pose = POSES.find((p) => p.id === id);
  const body = $('#shoot-body');
  const progress = $('#shoot-progress');
  if (!pose) return;

  progress.textContent = `${state.shootIndex + 1} / ${state.shootIds.length}`;
  const done = Boolean(store.getChecklist()[id]);

  body.innerHTML = `
    <div class="shoot__art">${poseVisual(pose, 'pose-visual pose-visual--shoot')}</div>
    <p class="shoot__eyebrow">${pose.couple ? '双人' : '单人'} · ${escapeHtml(pose.duration)}${done ? ' · 已勾选' : ''}</p>
    <h2 class="shoot__title">${escapeHtml(pose.name)}</h2>
    <ol class="shoot__steps">
      ${pose.steps.map((s, i) => `<li><span>${i + 1}</span>${escapeHtml(s)}</li>`).join('')}
    </ol>
    <p class="shoot__tip"><strong>避坑：</strong>${escapeHtml(pose.tips[0] || '')}</p>
    <p class="shoot__camera">${escapeHtml(pose.camera)}</p>
  `;

  $('#shoot-prev').disabled = state.shootIndex <= 0;
  $('#shoot-next').textContent =
    state.shootIndex >= state.shootIds.length - 1 ? '完成全部' : '下一个';
}

function openShoot(ids, startIndex = 0) {
  if (!ids.length) {
    showToast('请先加入必拍姿势，或选一组短拍快选');
    return;
  }
  closeDrawer();
  state.shootIds = ids;
  state.shootIndex = Math.min(Math.max(0, startIndex), ids.length - 1);
  const el = $('#shoot-mode');
  el.hidden = false;
  document.body.classList.add('shoot-open');
  renderShoot();
}

function closeShoot() {
  $('#shoot-mode').hidden = true;
  document.body.classList.remove('shoot-open');
  renderChecklist();
  renderGrid();
}

function markCurrentShotDone() {
  const id = state.shootIds[state.shootIndex];
  if (!id) return;
  const map = store.getChecklist();
  if (!(id in map)) store.addToChecklist(id);
  const next = store.getChecklist();
  if (!next[id]) store.toggleChecklistDone(id);
  showToast('已标记拍完');
  renderShoot();
}

function bindEvents() {
  $('#category-filters').addEventListener('click', (e) => {
    const btn = e.target.closest('[data-category]');
    if (!btn) return;
    state.category = btn.dataset.category;
    renderFilters();
    renderGrid();
  });

  $('#pose-search').addEventListener('input', (e) => {
    state.query = e.target.value;
    renderGrid();
  });

  $('#fav-only').addEventListener('click', (e) => {
    state.favOnly = !state.favOnly;
    e.currentTarget.setAttribute('aria-pressed', String(state.favOnly));
    e.currentTarget.classList.toggle('is-active', state.favOnly);
    renderGrid();
  });

  $('#couple-only').addEventListener('click', (e) => {
    state.coupleOnly = !state.coupleOnly;
    e.currentTarget.setAttribute('aria-pressed', String(state.coupleOnly));
    e.currentTarget.classList.toggle('is-active', state.coupleOnly);
    renderGrid();
  });

  $('#pose-grid').addEventListener('click', (e) => {
    const favBtn = e.target.closest('[data-fav]');
    if (favBtn) {
      e.stopPropagation();
      store.toggleFavorite(favBtn.dataset.fav);
      renderGrid();
      renderChecklist();
      showToast(store.isFavorite(favBtn.dataset.fav) ? '已加入收藏' : '已取消收藏');
      return;
    }
    const card = e.target.closest('[data-pose-id]');
    if (card) openDrawer(card.dataset.poseId);
  });

  $('#pose-grid').addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const card = e.target.closest('[data-pose-id]');
    if (!card) return;
    e.preventDefault();
    openDrawer(card.dataset.poseId);
  });

  $('#pose-drawer').addEventListener('click', (e) => {
    if (e.target.closest('[data-close-drawer]')) {
      closeDrawer();
      return;
    }
    const favBtn = e.target.closest('[data-fav]');
    if (favBtn) {
      store.toggleFavorite(favBtn.dataset.fav);
      openDrawer(favBtn.dataset.fav);
      renderGrid();
      showToast(store.isFavorite(favBtn.dataset.fav) ? '已加入收藏' : '已取消收藏');
      return;
    }
    const shootOne = e.target.closest('[data-shoot-one]');
    if (shootOne) {
      openShoot([shootOne.dataset.shootOne], 0);
      return;
    }
    const addBtn = e.target.closest('[data-add-list]');
    if (addBtn) {
      const id = addBtn.dataset.addList;
      if (id in store.getChecklist()) {
        showToast('已在必拍清单中');
      } else {
        store.addToChecklist(id);
        showToast('已加入今日必拍');
      }
      openDrawer(id);
      renderChecklist();
      renderGrid();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (!$('#shoot-mode').hidden) {
        closeShoot();
        return;
      }
      if (!$('#pose-drawer').hidden) closeDrawer();
    }
    if ($('#shoot-mode').hidden) return;
    if (e.key === 'ArrowRight') $('#shoot-next').click();
    if (e.key === 'ArrowLeft') $('#shoot-prev').click();
  });

  $('#checklist-list').addEventListener('change', (e) => {
    const input = e.target.closest('[data-check]');
    if (!input) return;
    store.toggleChecklistDone(input.dataset.check);
    renderChecklist();
  });

  $('#checklist-list').addEventListener('click', (e) => {
    const rm = e.target.closest('[data-remove-list]');
    if (!rm) return;
    store.removeFromChecklist(rm.dataset.removeList);
    renderChecklist();
    renderGrid();
    showToast('已移出清单');
  });

  $('#suggest-eight')?.addEventListener('click', () => {
    store.setChecklistIds(SUGGESTED_EIGHT);
    renderChecklist();
    renderGrid();
    showToast('已载入短拍推荐');
    $('#checklist').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  const packsRoot = $('#short-packs');
  if (packsRoot) {
    packsRoot.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pack]');
      if (!btn) return;
      const pack = SHORT_PACKS.find((p) => p.id === btn.dataset.pack);
      if (!pack) return;
      store.setChecklistIds(pack.ids);
      renderChecklist();
      renderGrid();
      showToast(`已载入：${pack.label}`);
    });
  }

  $('#clear-checklist').addEventListener('click', () => {
    store.clearChecklist();
    renderChecklist();
    renderGrid();
    showToast('清单已清空');
  });

  const startShoot = () => {
    const queue = getShootQueue();
    if (!Object.keys(store.getChecklist()).length) {
      store.setChecklistIds(SUGGESTED_EIGHT);
      renderChecklist();
      showToast('已自动载入推荐 8 个');
    }
    openShoot(getShootQueue(), 0);
  };

  $('#start-shoot').addEventListener('click', startShoot);
  $('#hero-shoot').addEventListener('click', startShoot);
  $('#bottom-shoot').addEventListener('click', startShoot);

  $('#shoot-exit').addEventListener('click', closeShoot);
  $('#shoot-done-btn').addEventListener('click', markCurrentShotDone);
  $('#shoot-prev').addEventListener('click', () => {
    if (state.shootIndex <= 0) return;
    state.shootIndex -= 1;
    renderShoot();
  });
  $('#shoot-next').addEventListener('click', () => {
    if (state.shootIndex >= state.shootIds.length - 1) {
      markCurrentShotDone();
      closeShoot();
      showToast('本轮拍摄完成，真棒');
      $('#checklist').scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    markCurrentShotDone();
    state.shootIndex += 1;
    renderShoot();
  });

  // swipe in shoot mode
  let touchX = null;
  $('#shoot-mode').addEventListener(
    'touchstart',
    (e) => {
      touchX = e.changedTouches[0].clientX;
    },
    { passive: true }
  );
  $('#shoot-mode').addEventListener(
    'touchend',
    (e) => {
      if (touchX == null) return;
      const dx = e.changedTouches[0].clientX - touchX;
      touchX = null;
      if (Math.abs(dx) < 56) return;
      if (dx < 0) $('#shoot-next').click();
      else $('#shoot-prev').click();
    },
    { passive: true }
  );
}

function renderShortPacks() {
  const root = $('#short-packs');
  if (!root) return;
  root.innerHTML = SHORT_PACKS.map(
    (p) => `
    <button type="button" class="pack-card" data-pack="${escapeHtml(p.id)}">
      <strong>${escapeHtml(p.label)}</strong>
      <span>${escapeHtml(p.desc)} · ${p.ids.length} 张</span>
    </button>`
  ).join('');
}

function applyUrlDeepLink() {
  try {
    const params = new URLSearchParams(window.location.search);
    const packId = params.get('pack');
    const scene = params.get('scene');
    if (scene && CATEGORIES.some((c) => c.id === scene)) {
      state.category = scene;
      renderFilters();
      renderGrid();
    }
    if (packId) {
      const pack = SHORT_PACKS.find((p) => p.id === packId);
      if (pack) {
        store.setChecklistIds(pack.ids);
        renderChecklist();
        renderGrid();
        showToast(`已载入行程短拍：${pack.label}`);
        document.getElementById('checklist')?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  } catch {
    /* ignore */
  }
}

function initApp() {
  renderIntroStats();
  renderFilters();
  renderGrid();
  renderShortPacks();
  renderChecklist();
  renderPrep();
  renderRefs();
  bindEvents();
  applyUrlDeepLink();

  if (!Object.keys(store.getChecklist()).length) {
    setTimeout(() => showToast('提示：选「随便拍 3 张」即可开始，不必一天拍完', 3400), 1200);
  }
}

initApp();
